<template>
  <div class="local-controls pb-2">
    <div class="self-video-block">
      <div class="video-wrapper" id="main-video" ref="localVideo"></div>
      <div class="self-video-placeholder" v-if="videoStopped">
        <room-avatar :takePictureDisabled="videoStopped" />
      </div>
      <div
        class="video-disabled-tracks"
        v-if="isHost && (audioStopped || videoStopped)"
      >
        <v-icon color="error" v-if="audioStopped">mic_off</v-icon>
        <v-icon color="error" v-if="videoStopped">videocam_off</v-icon>
      </div>
    </div>

    <div class="pl-1" v-if="!disabled">
      <v-btn
        small
        elevation="0"
        class="mt-2 mr-1"
        @click="settings"
        v-if="!isMobile"
      >
        <template v-if="viewingSettings">
          <v-icon small>chat</v-icon> chat
        </template>
        <template v-else>
          <v-icon small elevation="0" class="ml-0">settings</v-icon>
        </template>
      </v-btn>

      <template v-if="!room">
        <v-btn
          small
          elevation="0"
          class="mt-2 mr-1"
          @click="updateVideoPreview"
        >
          <v-icon small>videocam</v-icon> Preview
        </v-btn>
        <v-btn
          small
          elevation="0"
          class="mt-2 mr-1 primary animated"
          @click="join"
          id="join-room"
          :loading="joiningRoom"
          :disabled="joiningRoom"
          color="primary"
        >
          <v-icon small>group</v-icon> Join
        </v-btn>
      </template>

      <template v-else>
        <v-btn small elevation="0" class="mt-2 mr-1 red" @click="disconnect">
          <v-icon small>close</v-icon> End
        </v-btn>

        <div style="display: inline-block" v-if="isHost">
          <v-btn
            small
            elevation="0"
            class="mt-2 mr-1"
            @click="toggleSingleTrack('audio', audioStopped)"
            :loading="audioChanging"
            :disabled="audioChanging"
          >
            <template v-if="audioStopped">
              <v-icon small>mic_off</v-icon> Unmute
            </template>
            <template v-else><v-icon small>mic</v-icon> Mute</template>
          </v-btn>
          <v-btn
            elevation="0"
            small
            class="mt-2 mr-1"
            @click="toggleSingleTrack('video', videoStopped)"
          >
            <template v-if="videoStopped">
              <v-icon small>videocam_off</v-icon> Start
            </template>
            <template v-else> <v-icon small>videocam</v-icon> Stop </template>
          </v-btn>
        </div>
      </template>

      <room-avatar
        class="d-inline-block"
        showButton
        :takePictureDisabled="videoStopped"
      />

      <local-settings
        ref="settings"
        v-show="viewingSettings || isMobile"
        @switchDevice="switchDevice"
        @userMediaError="handleUserMediaError"
      />
    </div>
  </div>
</template>

<script>
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  createLocalTracks,
  LocalVideoTrack,
  LocalAudioTrack,
} from 'twilio-video';

import { eventBus } from '@/lib/events';
import { mapState, mapGetters } from 'vuex';

import LocalSettings from './LocalSettings';
import RoomAvatar from '@/components/rooms/components/RoomAvatar';

export default {
  name: 'RoomControls',
  components: {
    LocalSettings,
    RoomAvatar,
  },
  props: {
    room: {
      type: Object,
      default: () => ({}),
    },
    joiningRoom: Boolean,
    disabled: Boolean,
  },
  data() {
    return {
      localTracks: null,
      viewingSettings: false,
      audioinput: null,
      videoinput: null,
      audioStopped: false,
      audioChanging: false,
      videoStopped: false,
      videoChanging: false,
      trackNameSuffixes: {
        screenshare_audio: 'sa',
        screenshare_video: 'sv',
        video: 'v',
        audio: 'a',
        videoinput: 'v',
        audioinput: 'a',
        data: 'd',
      },
      preparingToJoin: false,
    };
  },

  watch: {
    room() {
      this.checkAvatar();
    },
    videoinput() {
      this.checkAvatar();
    },
    'roomInfo.identity'(identity) {
      if (!identity) return;
      this.autoJoin();
    },
  },

  computed: {
    ...mapGetters('rooms', ['interviewFinished']),
    ...mapState('rooms', ['roomInfo']),
    isHost() {
      let routeQuery = this.$route ? this.$route.query : {};
      return (
        (this.roomInfo?.identity || '').includes('host') ||
        routeQuery.type === 'host'
      );
    },
  },

  methods: {
    attachTracks(container, tracks) {
      if (container.querySelector('video') || !tracks) return;

      tracks.forEach((track) => {
        if (track.kind !== 'data' && !track.screenshare) {
          container.appendChild(track.attach());
        }
      });
    },

    getLocalTracks() {
      //returns promise
      if (this.localTracks) {
        return this.localTracks;
      }

      //a host can use whatever source video they want
      //but a guest must have max resolution of 1280 by 720
      // const video = this.isHost ? true : ({
      //   width: { max : 1280 },
      //   height: { max : 720 },
      // });

      return (this.localTracks = createLocalTracks({
        audio: !this.audioStopped,
        video: true,
      }).catch((error) => {
        this.handleUserMediaError(error);
      }));
    },

    updateVideoPreview() {
      //remove all local tracks because going to replace with
      //new ones
      let container = this.$refs.localVideo;

      if (!container) return false;

      let existingElements = container.querySelectorAll('*');
      if (existingElements.length) {
        existingElements.forEach((el) => el.remove());
      }

      this.getLocalTracks().then((tracks) => {
        this.attachTracks(container, tracks);
      });
    },

    join() {
      this.getLocalTracks().then((tracks) => {
        if (tracks) {
          this.attachTracks(this.$refs.localVideo, tracks);
          //we are not going to give tracks directly
          //we are going to recreate them when we join
          //we do this because setting priority on tracks
          //is easier when publishing tracks
          this.$emit('join', { tracks: [] });
        }
      });
    },

    disconnect() {
      this.toggleTracks(false);
      this.$emit('disconnect');
    },

    settings() {
      this.viewingSettings = !this.viewingSettings;
      this.$emit('viewingSettings', this.viewingSettings);
    },

    unpublishTrackByKind(room, kind, screenshare = false) {
      let unpublishedTracks = [];
      (room?.localParticipant?.tracks || []).forEach((trackPublication) => {
        const pubTrack = trackPublication?.track;
        if (
          trackPublication.kind === kind &&
          !!pubTrack?.screenshare == screenshare
        ) {
          pubTrack.stop();
          const unpubTrack = room.localParticipant.unpublishTrack(pubTrack);
          if (unpubTrack) {
            unpublishedTracks.push(unpubTrack);
          }
        }
      });

      return unpublishedTracks;
    },

    timeout(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    getTrackName(kind) {
      let name = this.roomInfo.identity || '';
      if (kind) {
        let suffix = this.trackNameSuffixes[kind];
        if (suffix) {
          name += '_' + suffix;
        }
      }
      return name;
    },

    async updatePublishedTrack(room, track) {
      let unpublishedTracks = this.unpublishTrackByKind(
        room,
        track.kind,
        track.screenshare
      );

      await this.timeout(100);

      let trackPublished = await room.localParticipant.publishTrack(track, {
        priority: this.getTrackPriority(track),
      });

      return trackPublished;
    },

    getTrackPriority() {
      return this.isHost ? 'high' : 'standard';
    },

    async changeTracks(kind, payload = {}) {
      const isScreenShare = kind.includes('screenshare');
      let tracksFilter = (t) => t.kind !== kind;
      let createTrack;

      if (kind === 'audioinput' || kind === 'audio') {
        createTrack = createLocalAudioTrack;
      }

      if (kind === 'videoinput' || kind === 'video') {
        createTrack = createLocalVideoTrack;
      }

      if (isScreenShare) {
        tracksFilter = ({ name }) => {
          return !name.endsWith('_s' + (payload.kind || '').charAt(0));
        };

        let method =
          payload.kind === 'video' ? LocalVideoTrack : LocalAudioTrack;

        createTrack = async (_payload) => {
          return new Promise((resolve) => {
            let track = new method(_payload, {
              name: _payload.name,
            });
            track.screenshare = true;
            resolve(track);
          });
        };
      }

      let tracks = [];
      try {
        tracks = await this.getLocalTracks();
      } catch (error) {
        console.error(error);
        return false;
      }

      tracks = tracks.filter((track) => {
        return tracksFilter(track);
      });
      payload.name = this.getTrackName(kind);

      const newTrack = await createTrack(payload);
      tracks.push(newTrack);
      this.localTracks = new Promise((resolve) => resolve(tracks));

      this.updateVideoPreview();
      if (this.room && newTrack) {
        await this.updatePublishedTrack(this.room, newTrack);
      }

      if ((kind || '').includes('screenshare')) {
        eventBus.trigger('createScreenShareTrack', newTrack);
      }

      return true;
    },

    checkAvatar() {
      if (!this.room || !this.videoinput) return;
      eventBus.trigger('checkRoomProfileSettings');
    },

    switchDevice({ deviceId, kind }) {
      if (kind === 'audiooutput') {
        return eventBus.trigger('audioOutputChanged', { deviceId });
      }

      let payload = {
        deviceId: { exact: deviceId },
      };

      this[kind] = payload;

      if (kind.includes('audio') && this.audioStopped) {
        return;
      }

      this.changeTracks(kind, payload);
    },

    checkMedia() {
      if (!this.audioinput && !this.videoinput && this.$refs.settings) {
        this.$refs.settings.initMedia();
        return false;
      }
      return true;
    },

    startTracks() {
      if (!this.checkMedia()) return;
      this.toggleTracks(true, true);
    },

    stopTracks() {
      this.toggleTracks(false);
      setTimeout(() => {
        this.toggleTracks(false);
      }, 100);
    },

    toggleTracks(start = true, stopAudio) {
      const tracks = ['audio', 'video'];
      tracks.forEach((type) => {
        let startTrack = start;
        if (type === 'audio' && this.isHost && stopAudio) {
          startTrack = false;
        }

        this.toggleSingleTrack(type, startTrack);
      });
    },

    async toggleSingleTrack(type = '', start = true) {
      const stopKey = `${type}Stopped`;
      const changeKey = `${type}Changing`;

      this[changeKey] = true;

      if (start) {
        await this.changeTracks(type, this[`${type}input`]);
        this[stopKey] = false;
      } else {
        this[stopKey] = true;
        this.unpublishTrackByKind(this.room, type);
      }

      this[changeKey] = false;

      return true;
    },

    handleUserMediaError(err) {
      eventBus.trigger('showRoomInfoDialog', {
        mediaError: err,
      });
    },

    toggleScreenShareTrack(payload, kind) {
      this.unpublishTrackByKind(this.room, kind, true);
      if (payload) {
        this.changeTracks(`screenshare_${kind}`, payload);
      }
    },

    checkOnlineStatus() {
      const online = navigator.onLine;
      if (online && this.room) {
        setTimeout(() => {
          this.startTracks();
        }, 5000);
      }
    },

    async autoJoin() {
      await this.$nextTick();
      if (this.$route?.query?.action === 'join' && !this.interviewFinished) {
        this.join();
      }
    },
  },

  created() {
    eventBus.on('stopTracks', () => {
      this.stopTracks();
    });

    eventBus.on('startTracks', () => {
      this.startTracks();
    });

    eventBus.on('shareScreenVideo', (payload) => {
      this.toggleScreenShareTrack(payload, 'video');
    });

    eventBus.on('shareScreenAudio', (payload) => {
      this.toggleScreenShareTrack(payload, 'audio');
    });

    window.addEventListener('online', this.checkOnlineStatus);
    window.addEventListener('offline', this.checkOnlineStatus);
  },
};
</script>

<style lang="less">
.local-controls {
  box-shadow: @content-shadow;
  z-index: 1;

  .v-btn__content {
    font-size: 11px !important;
    .v-icon {
      margin-right: 4px;
    }
  }
}

.self-video-block {
  position: relative;

  video {
    transform: scale(-1, 1);
  }
}

.video-disabled-tracks {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 4px;

  .v-icon + .v-icon {
    margin-left: 8px;
  }
}

.self-video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .room-avatar {
    height: 180px !important;
    width: 180px !important;

    svg,
    img {
      height: 180px;
      width: 180px;
    }
  }
}
</style>
